import { useCallback, useRef } from 'react';

/**
 * Hendelseshåndterer med PERMANENT stabil identitet, men som alltid kaller nyeste
 * versjon av funksjonen.
 *
 * HVORFOR: en `useCallback` med avhengighetsliste bytter identitet hver gang en
 * avhengighet endrer seg. For props som sendes ned i memoiserte komponenter er det
 * nok til å slå memoiseringen ut. Konkret tilfelle (fikset 23.08.2026):
 * `onSelectSpot` til `MapComponent` var en inline-arrow, som gjorde
 * `React.memo(ClusteredSpotMarkers)` nytteløs — og da kjørte react-leaflet
 * `setIcon()` + `setLatLng()` på HVER markør ved HVER panorering. Se
 * `components/map/markerIconCache.ts` for hele kjeden.
 *
 * Alternativet — å liste opp alle avhengighetene i en `useCallback` — ville gitt
 * identitetsbytte hver gang én av dem endret seg, OG risiko for utdatert closure
 * hvis lista ble feil. Ref-en her har ingen av begge problemene.
 *
 * Brukes kun for HENDELSER (trykk, callbacks fra Leaflet/DOM), ikke for verdier som
 * leses under render — en ref er bevisst usynlig for React sin render-avhengighet.
 */
export function useEventCallback<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const ref = useRef(fn);
  // Skrives under render slik at en hendelse som fyrer FØR effektene er flushet
  // (typisk første trykk rett etter mount) også får nyeste versjon. Tilordningen er
  // idempotent, så StrictMode sin dobbeltrender er ufarlig.
  ref.current = fn;
  return useCallback((...args: A): R => ref.current(...args), []);
}

export default useEventCallback;
